import { useState, type ReactNode } from 'react';
import { drawText } from '../elements/flex_label.ts';
import { drawIcon, type IconDefinition } from '../elements/icon.ts';
import { cssColor } from '../elements/color.ts';
import { CanvasView } from './canvas_view.tsx';
import { DEFAULT_TILE_STYLE, type ResolvedTileStyle } from './tile_style.ts';
import { useLegacyIcon } from './icons.ts';
import {
    TILE_FONT_FAMILY,
    TILE_FONT_METRICS,
    TILE_RADIUS,
    TITLE_LEFT_MARGIN,
    TITLE_ROW_HEIGHT,
    TITLE_STYLE,
    TITLE_TEXT_HEIGHT,
} from './theme.ts';

/** How much of a stale or failing tile's content shows through (the owner's choice, Phase 1 C3). */
const DIMMED_OPACITY = 0.4;
const WARNING_COLOR = '#ffa500';
/** The legacy `TileView`: a red border of 2 while the tile is selected in edit mode. */
export const SELECTED_OUTLINE = '2px solid #ff0000';
/** How much of its background the icon fills, where it has one (TS-11). */
const ICON_IN_BACKGROUND = 0.6;

/**
 * Where the tile's icon goes (TS-10 to TS-13, issue #21), apart from the title: a square of `size`
 * in a corner or the middle of the top or bottom edge of the tile's box inside its border, the
 * tile's padding and the icon's margin from its edges. Drawn over the title and the content.
 */
export interface TileIconPlace {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
    /** From the edges it is at: the padding and the margin. */
    inset: number;
    size: number;
    /** The icon itself: the square, or less of it inside a background. */
    glyph: number;
    background: string | undefined;
    /** At most half the size: a circle. */
    radius: number;
}

export function tileIconPlace(style: ResolvedTileStyle) : TileIconPlace {
    const { iconSize: size } = style;
    const [vertical, horizontal] = style.iconPosition.split('-') as [TileIconPlace['vertical'], TileIconPlace['horizontal']];
    const background = paint(style.iconBackground);

    return {
        vertical,
        horizontal,
        inset: style.padding + style.iconMargin,
        size,
        glyph: background ? size * ICON_IN_BACKGROUND : size,
        background,
        radius: Math.min(style.iconRadius, size / 2),
    };
}

export interface TileFrameProps {
    label?: string | undefined;
    /** The tile's icon, by its legacy or Font Awesome name (the legacy `titleIcon`); where and how, the tile style's (TS-10). */
    titleIcon?: string | undefined;
    /** Selected in edit mode (Phase 3): outlined. */
    selected?: boolean;
    /** Stale or failing data: the content is dimmed. */
    dimmed?: boolean;
    /** Why sources fail; a warning mark shows them on a tap. Empty: no mark. */
    failures?: readonly string[];
    /** Instead of the tile's background, while a view asks for it (`useTileHighlight`). */
    background?: string | undefined;
    /** The tile's style, resolved (dashboard-editor spec §5.9): the defaults where none is given. */
    tileStyle?: ResolvedTileStyle | undefined;
    /** What a tap anywhere on the tile does (its `tap`, tiles spec §6); the warning mark keeps its own. */
    onTap?: (() => void) | undefined;
    children?: ReactNode;
}

/**
 * The tile's icon on a layer of its own over the title and the content (TS-12), in its background
 * where it has one. Taps go through it to the tile.
 */
function TileIcon({ icon, place, color }: { icon: IconDefinition; place: TileIconPlace; color: string }) : ReactNode {
    const across = place.horizontal === 'center' ? { left: '50%', transform: 'translateX(-50%)' } : { [place.horizontal]: place.inset };

    return (
        <div
            style={{
                position: 'absolute',
                [place.vertical]: place.inset,
                ...across,
                width: place.size,
                height: place.size,
                borderRadius: place.radius,
                background: place.background ?? 'transparent',
                zIndex: 1,
                pointerEvents: 'none',
            }}
        >
            <CanvasView
                deps={[icon, place.size, place.glyph, color]}
                draw={(ctx, width, height) => {
                    // Its glyph's box fitted, so that every icon of a size is as large (not its ink, as the legacy title's).
                    const box = { left: (width - place.glyph) / 2, top: (height - place.glyph) / 2, width: place.glyph, height: place.glyph };
                    drawIcon(ctx, icon, box, color, { align: 'center', valign: 'center' });
                }}
            />
        </div>
    );
}

/** A colour as CSS, or nothing for "none" (TS-6). */
const paint = (color: string) : string | undefined => (color === 'none' ? undefined : cssColor(color));

/** The tile's drop shadow as CSS (DS-5); none when off. */
export function shadowOf(style: ResolvedTileStyle) : string | undefined {
    return style.shadow ? `${style.shadowX}px ${style.shadowY}px ${style.shadowBlur}px ${cssColor(style.shadowColor)}` : undefined;
}

/**
 * The tile's box as CSS: its background, border and shadow, or none of them where the box is
 * switched off (DS-8: a tile laid over others), whatever they are set to. A hidden border keeps its
 * width, so the content stays where it was. `highlight` (a tap's flash) shows either way.
 */
export function boxOf(style: ResolvedTileStyle, highlight?: string | undefined) : { background: string; border: string; boxShadow: string | undefined } {
    const shown = style.showBackground;
    const border = paint(style.border);
    const width = border ? style.borderWidth : 0;

    return {
        background: highlight ?? (shown ? paint(style.background) : undefined) ?? 'transparent',
        border: width > 0 ? `${width}px solid ${shown ? border : 'transparent'}` : 'none',
        boxShadow: shown ? shadowOf(style) : undefined,
    };
}

/**
 * The legacy `TileView`: rounded frame, a fitted, upper-cased title row, the content below. Added
 * in Phase 1: dimmed content when the data is stale or failing, and a warning mark for failures.
 */
export function TileFrame({ label, titleIcon, selected = false, dimmed = false, failures = [], background, tileStyle = DEFAULT_TILE_STYLE, onTap, children }: TileFrameProps) : ReactNode {
    const icon = useLegacyIcon(titleIcon);
    const iconPlace = tileIconPlace(tileStyle);
    const title = tileStyle.titleUppercase ? label?.toUpperCase() : label;
    const { padding, radius, titleHeight, titleAlign } = tileStyle;
    const titleColor = tileStyle.title;
    const titleBackground = paint(tileStyle.titleBackground);
    // The title's text keeps its legacy proportion to the row (20 of 26).
    const scale = titleHeight / TITLE_ROW_HEIGHT;
    const rowHeight = titleBackground ? padding + titleHeight : titleHeight;
    const [showFailures, setShowFailures] = useState(false);
    const failing = failures.length > 0;

    return (
        <div
            data-tile
            // The title is drawn on a canvas: this names the tile to scripts that find it (scripts/browser-check).
            data-tile-label={label}
            onClick={onTap}
            style={{
                position: 'relative',
                boxSizing: 'border-box',
                width: '100%',
                height: '100%',
                padding,
                borderRadius: radius,
                // The shadow is outside the box: `overflow` clips the content, not the tile's own shadow.
                ...boxOf(tileStyle, background),
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                outline: selected ? SELECTED_OUTLINE : 'none',
                outlineOffset: -2,
                cursor: onTap ? 'pointer' : undefined,
            }}
        >
            {title && titleBackground && (
                // Out to the tile's edges, not inside its padding (TS-3): over the padding above the title too.
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: padding + titleHeight, background: titleBackground }} />
            )}
            {title && (
                // Positioned, so that it is drawn over the title's background. With one, the row reaches up
                // over the padding, and the title is centred in the whole band (issue #12).
                <div style={{ position: 'relative', height: rowHeight, marginTop: titleBackground ? -padding : 0, flex: 'none' }}>
                    <CanvasView
                        deps={[title, titleColor, titleHeight, rowHeight, titleAlign]}
                        draw={(ctx, width) => {
                            // The capitals' height: the title is upper case, so they are what is seen.
                            const textHeight = TITLE_TEXT_HEIGHT * scale;
                            const capitals = (textHeight * TILE_FONT_METRICS.capHeight) / (TILE_FONT_METRICS.capHeight + TILE_FONT_METRICS.descent);
                            // The whole row: the icon is apart from the title, drawn over it where they meet (TS-10).
                            // The legacy margin at the left; at the right as well, for a title aligned there (TS-14).
                            const left = titleAlign === 'right' ? 0 : TITLE_LEFT_MARGIN;
                            const box = { left, top: titleBackground ? (rowHeight - capitals) / 2 : 0, width: width - TITLE_LEFT_MARGIN, height: textHeight };
                            drawText(ctx, title, box, titleColor, { ...TITLE_STYLE, align: titleAlign });
                        }}
                    />
                </div>
            )}
            <div style={{ flex: 1, minHeight: 0, position: 'relative', opacity: dimmed ? DIMMED_OPACITY : 1, transition: 'opacity 300ms' }}>
                {children}
            </div>
            {icon && <TileIcon icon={icon} place={iconPlace} color={tileStyle.iconColor ?? titleColor} />}
            {failing && (
                <button
                    type="button"
                    aria-label="Show why this tile has no current data"
                    onClick={(event) => {
                        event.stopPropagation();
                        setShowFailures((shown) => !shown);
                    }}
                    style={{
                        position: 'absolute',
                        top: 2,
                        // Left of the icon, where it is at the top right.
                        right: icon && iconPlace.vertical === 'top' && iconPlace.horizontal === 'right' ? iconPlace.inset + iconPlace.size + 4 : 4,
                        padding: '0 4px',
                        border: 'none',
                        background: 'transparent',
                        color: WARNING_COLOR,
                        font: `18px ${TILE_FONT_FAMILY}`,
                        lineHeight: '22px',
                        cursor: 'pointer',
                    }}
                >
                    ⚠
                </button>
            )}
            {failing && showFailures && (
                <div
                    role="status"
                    onClick={(event) => {
                        event.stopPropagation();
                        setShowFailures(false);
                    }}
                    style={{
                        position: 'absolute',
                        left: 4,
                        right: 4,
                        bottom: 4,
                        maxHeight: '70%',
                        overflow: 'auto',
                        padding: '6px 8px',
                        borderRadius: TILE_RADIUS,
                        background: 'rgba(0, 0, 0, 0.85)',
                        border: `1px solid ${WARNING_COLOR}`,
                        color: '#ffffff',
                        font: `13px ${TILE_FONT_FAMILY}`,
                        lineHeight: 1.35,
                        overflowWrap: 'anywhere',
                    }}
                >
                    {failures.map((failure) => (
                        <div key={failure}>{failure}</div>
                    ))}
                </div>
            )}
        </div>
    );
}
